// The caller's access in the current workspace, exactly as the server
// resolved it (`GET /api/me` → `access`, from services/permissions.js). The
// rail and every control read this, never the role name, so what a screen
// offers is what the server will accept (D1.1, D1.7). It only ever hides:
// the server still refuses anything the caller cannot do, whatever the
// screen shows.
//
// With no access known yet (an older server, or /api/me still answering for
// another workspace) nothing is hidden: the server's refusal is then the
// only answer, which is still correct.

import { createContext, useContext } from 'react'

export type Level = 'none' | 'read' | 'edit' | 'add'

export type Area =
  | 'dashboard'
  | 'personnel'
  | 'groups'
  | 'teams'
  | 'timesheets'
  | 'wages'
  | 'sites'
  | 'product_activity'
  | 'batches'
  | 'reports'
  | 'quality'
  | 'processing_rules'
  | 'integrations'
  | 'general_settings'
  | 'label_printing'
  | 'config_stock_dispatch'

export type Power = 'payroll_export' | 'day_close' | 'day_lock' | 'shared_reports' | 'manage_sign_in' | 'grant_access' | 'pay_data' | 'report_settings'

export type Capability =
  | 'view_stock_orders'
  | 'manage_stock_dispatch'
  | 'create_edit_orders'
  | 'approve_orders'
  | 'view_manage_pricing'
  | 'create_xero_invoices'

// D2 (Kerrin, 2026-10-01): the tenant's one product and the Stock &
// Dispatch licence, as the server resolved them. An older server sends none
// of these, and then nothing is hidden (the server still refuses).
export type Product = 'grow' | 'packright'
export type ModuleState = 'licensed' | 'not_licensed' | 'not_available' | 'unconfigured'
export type Modules = { stock_dispatch: ModuleState }
// The PackRight packing lists and the level on each; null off PackRight.
export type Packing = { box_types: Level; lids: Level }

export interface Access {
  role: string
  areas: Partial<Record<Area, Level>>
  capabilities: Capability[]
  capability_source: 'grants' | 'dataphyll_support'
  powers: Power[]
  report_scope: 'managed_groups' | 'tenant' | null
  membership_id: string | null
  product?: Product | null
  modules?: Modules
  // The unlicensed page and its enquiry (§7o.2): ADMIN and SYSADMIN only.
  module_preview?: boolean
  packing?: Packing | null
}

// WorkspaceProvider provides it. A screen reads it with useAccess(); outside
// a provider (a screen rendered on its own) it is simply not known.
export const AccessContext = createContext<Access | null>(null)
export const useAccess = (): Access | null => useContext(AccessContext)

const RANK: Record<Level, number> = { none: 0, read: 1, edit: 2, add: 3 }

export function isAccess(x: unknown): x is Access {
  const a = x as Access
  return !!a && typeof a === 'object' && typeof a.role === 'string' && !!a.areas && Array.isArray(a.powers) && Array.isArray(a.capabilities)
}

// May the caller hold `level` on `area`? Unknown access answers yes.
export function can(access: Access | null | undefined, area: Area, level: Level = 'read'): boolean {
  if (!access) return true
  return RANK[access.areas[area] || 'none'] >= RANK[level]
}

export function hasPower(access: Access | null | undefined, power: Power): boolean {
  return !access || access.powers.includes(power)
}

export function hasCapability(access: Access | null | undefined, cap: Capability): boolean {
  return !access || access.capabilities.includes(cap)
}

// The Stock & Dispatch module's state (D2). Unknown access, or a server that
// does not say, reads as licensed: nothing is hidden, the server refuses.
export function moduleState(access: Access | null | undefined): ModuleState {
  return access?.modules?.stock_dispatch || 'licensed'
}

// The PackRight packing lists (box types, lids): off PackRight they are not
// shown at all. Unknown access shows them.
export function hasPacking(access: Access | null | undefined): boolean {
  return !access || access.packing !== null
}

// Stock & Dispatch is reached by capability, not by an area level, and only
// while the module is licensed. The unlicensed page is its own gate.
export const STOCK = 'stock_dispatch' as const
export const STOCK_PREVIEW = 'stock_preview' as const
export type Gate = Area | typeof STOCK | typeof STOCK_PREVIEW

export function canOpen(access: Access | null | undefined, gate: Gate): boolean {
  if (gate === STOCK) return moduleState(access) === 'licensed' && hasCapability(access, 'view_stock_orders')
  if (gate === STOCK_PREVIEW) return !!access?.module_preview
  return can(access, gate)
}

// Each screen, the area that opens it, and one read the server guards with
// that same area: the denied screen asks it, so what a direct link shows is
// the server's own refusal (D1.7), not the browser's guess.
export const SCREENS: { path: string; gate: Gate; probe: string }[] = [
  { path: '/dashboard', gate: 'dashboard', probe: '/api/dashboard/days' },
  { path: '/personnel', gate: 'personnel', probe: '/api/personnel/roles' },
  { path: '/groups', gate: 'groups', probe: '/api/groups' },
  { path: '/teams', gate: 'teams', probe: '/api/teams' },
  { path: '/timesheets', gate: 'timesheets', probe: '/api/timesheets/settings' },
  { path: '/queries', gate: 'timesheets', probe: '/api/queries?status=open' },
  { path: '/wages', gate: 'wages', probe: '/api/wages/settings' },
  { path: '/product-activity', gate: 'product_activity', probe: '/api/product-activity' },
  { path: '/batches', gate: 'batches', probe: '/api/batches/settings' },
  { path: '/reports', gate: 'reports', probe: '/api/reports' },
  { path: '/label-printing', gate: 'label_printing', probe: '/api/label-printing/settings' },
  { path: '/stock-dispatch/overview', gate: STOCK, probe: '/api/stock' },
  { path: '/stock-dispatch', gate: STOCK, probe: '/api/stock' },
  { path: '/stock-dispatch/orders', gate: STOCK, probe: '/api/stock' },
  { path: '/stock-dispatch/dispatches', gate: STOCK, probe: '/api/stock' },
  { path: '/stock-dispatch/settings', gate: 'config_stock_dispatch', probe: '/api/settings/establishment' },
  // Refused to anyone but ADMIN and SYSADMIN on an unlicensed PackRight
  // workspace; the module's own read says why.
  { path: '/stock-dispatch/preview', gate: STOCK_PREVIEW, probe: '/api/stock' },
  { path: '/sites-jobs', gate: 'sites', probe: '/api/sites' },
  { path: '/sites-map', gate: 'sites', probe: '/api/sites' },
  { path: '/picking-jobs', gate: 'sites', probe: '/api/sites' },
  { path: '/devices', gate: 'sites', probe: '/api/devices' },
  { path: '/integrations', gate: 'integrations', probe: '/api/integrations/types' },
  { path: '/integrations/api-keys', gate: 'integrations', probe: '/api/api-keys' },
  { path: '/quality-control', gate: 'quality', probe: '/api/quality' },
  { path: '/settings', gate: 'general_settings', probe: '/api/settings/tenant' },
]

export const screenOf = (path: string) => SCREENS.find((s) => s.path === path)

// Where "/" lands: the Dashboard, or the first screen the caller can open
// (a group manager's is Reports).
export function landingPath(access: Access | null | undefined): string {
  return SCREENS.find((s) => canOpen(access, s.gate))?.path || '/dashboard'
}
